import React from "react";
import { ArrowRight } from "@phosphor-icons/react";
import { RISK_META, TYPE_LABEL } from "../lib/risk";
import { useI18n } from "../lib/i18n";

export function FindingCard({ finding, index, onViewDoc }) {
  const { t, lang } = useI18n();
  const risk = RISK_META[finding.risk] || RISK_META.MEDIUM;
  const typeLabel = (TYPE_LABEL[finding.type] || { pt: finding.type, en: finding.type })[lang];
  const pos = finding.position || {};
  const showFontCompare =
    finding.type === "INVISIBLE_TEXT" && pos.predominant_font && pos.size;

  return (
    <div
      className="bg-white border border-slate-200 rounded-md p-6 transition-all duration-200 hover:-translate-y-[1px] hover:shadow-[0_10px_20px_-8px_rgba(10,17,40,0.12)]"
      data-testid={`finding-card-${index}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span
            className="w-1.5 h-10 rounded-full shrink-0"
            style={{ background: risk.color }}
          />
          <div>
            <h3 className="text-base font-bold text-navy leading-tight">{typeLabel}</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {t("find.page")} {finding.page} · {Math.round((finding.confidence || 0) * 100)}%
            </p>
          </div>
        </div>
        <span
          className="px-2.5 py-1 rounded text-[11px] font-bold tracking-widest shrink-0"
          style={{ color: risk.color, background: risk.bg }}
          data-testid={`finding-risk-${index}`}
        >
          {lang === "pt" ? risk.label : risk.labelEn}
        </span>
      </div>

      {showFontCompare && (
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2">
            <div className="label-caps">{t("find.predominant")}</div>
            <div className="text-sm font-semibold text-navy mt-0.5">
              {pos.predominant_font} {pos.predominant_size}
            </div>
          </div>
          <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2">
            <div className="label-caps">{t("find.found")}</div>
            <div className="text-sm font-semibold text-risk-high mt-0.5">
              {pos.font} {pos.size}
            </div>
          </div>
        </div>
      )}

      <div className="mt-4">
        <div className="label-caps mb-1.5">{t("find.snippet")}</div>
        <p className="text-sm text-navy bg-slate-50 border-l-2 border-slate-300 px-3 py-2 rounded-r font-mono leading-relaxed break-words">
          “{finding.text}”
        </p>
      </div>

      <div className="mt-4">
        <div className="label-caps mb-1">{t("find.reason")}</div>
        <p className="text-sm text-slate-600 leading-relaxed">{finding.reason}</p>
      </div>

      {onViewDoc && (
        <button
          onClick={() => onViewDoc(finding)}
          data-testid={`finding-view-doc-${index}`}
          className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-gold transition-colors duration-200"
        >
          {t("find.viewDoc")}
          <ArrowRight size={16} weight="bold" />
        </button>
      )}
    </div>
  );
}
